import { getParent, types, type Instance } from 'mobx-state-tree';
import type { IInspectorParent } from './inspector_parent';

/** The change keys stored by the state inspector. */
type StateChangeKey = 'name';

/**
 * Store the changes in the state inspector that are currently pending.
 */
const StateInspectorChanges = types
  .model('StateInspectorChanges', {
    name: types.optional(types.maybeNull(types.string), null),
  })
  .views((self) => ({
    // the inspector is dirty if any of the attributes has changes
    get dirty() {
      return self.name !== null;
    },
    isDirty(key: StateChangeKey) {
      return self[key] !== null;
    },
  }))
  .actions((self) => ({
    /**
     * Store a change for the given key.
     *
     * This action also receives the original value of the attribute and clears
     * the stored change if the new value for the change is the same as the
     * original value.
     */
    storeChange(key: StateChangeKey, original: string | null, change: string | null) {
      if (original !== change) {
        self[key] = change;
      } else {
        self[key] = null;
      }
    },
    discard() {
      self.name = null;
    },
  }));

/**
 * UI state for the state inspector
 */
const StateInspector = types
  .model('StateInspector', {
    // the pending changes
    changes: types.optional(StateInspectorChanges, () => StateInspectorChanges.create({})),
  })
  .views((self) => ({
    // We are dirty when we have changes
    get dirty() {
      return self.changes.dirty;
    },
  }))
  .actions((self) => ({
    // Discard our changes
    discardChanges() {
      self.changes.discard();
    },
    setProjectBusy(busy: boolean) {
      getParent<IInspectorParent>(self).setProjectBusy(busy);
    },
  }));

export type IStateInspector = Instance<typeof StateInspector>;

export default StateInspector;
